/**
 * Gallery filter dropdown — viewport flyout layer (works on all screen sizes).
 */

export const FILTER_PANEL_PADDING = 16;
export const FILTER_PANEL_GAP = 6;

/**
 * @param {HTMLElement} group
 * @returns {HTMLElement|null}
 */
export function getFilterPanel(group) {
	return group._filterPanel || group.querySelector('.gallery-filter-group__panel');
}

/**
 * @param {string} blockId
 * @returns {HTMLDivElement}
 */
export function ensureFilterFlyoutRoot(blockId) {
	let root = document.querySelector(
		`.gallery-filter-flyout-root[data-gallery-block-id="${blockId}"]`
	);

	if (!root) {
		root = document.createElement('div');
		root.className = 'gallery-filter-flyout-root';
		root.dataset.galleryBlockId = blockId;
		document.body.appendChild(root);
	}

	return root;
}

/**
 * @param {HTMLElement} filtersContainer
 * @returns {HTMLElement|null}
 */
export function getOpenFilterGroup(filtersContainer) {
	return filtersContainer.querySelector('.gallery-filter-group.is-filter-open');
}

/**
 * @param {HTMLElement} filtersContainer
 * @param {HTMLDivElement|null} flyoutRoot
 */
export function closeFilterDropdown(filtersContainer, flyoutRoot) {
	const group = getOpenFilterGroup(filtersContainer);
	if (!group) {
		return;
	}

	const panel = getFilterPanel(group);
	const toggle = group.querySelector('.gallery-filter-group__toggle');

	if (panel) {
		panel.hidden = true;
		panel.classList.remove('is-in-flyout');
		panel.removeAttribute('style');
		group.appendChild(panel);
		delete group._filterPanel;
	}

	group.classList.remove('is-filter-open');
	toggle?.setAttribute('aria-expanded', 'false');

	if (flyoutRoot) {
		flyoutRoot.replaceChildren();
	}
}

/**
 * Position panel inside the full-viewport flyout (coordinates = viewport pixels).
 *
 * @param {HTMLElement} panel
 * @param {HTMLElement} group
 * @param {HTMLDivElement} flyoutRoot
 */
export function layoutFilterPanel(panel, group, flyoutRoot) {
	const pad = FILTER_PANEL_PADDING;
	const gap = FILTER_PANEL_GAP;
	const rootW = flyoutRoot.clientWidth;
	const rootH = flyoutRoot.clientHeight;
	const anchor = group.getBoundingClientRect();

	panel.style.position = 'absolute';
	panel.style.boxSizing = 'border-box';
	panel.style.margin = '0';
	panel.style.minWidth = '12rem';
	panel.style.width = 'max-content';
	panel.style.maxWidth = `${Math.max(120, rootW - pad * 2)}px`;
	panel.style.overflow = 'auto';
	panel.style.left = `${pad}px`;
	panel.style.top = `${pad}px`;
	panel.style.maxHeight = `${rootH - pad * 2}px`;

	const panelWidth = panel.offsetWidth;
	const panelHeight = panel.offsetHeight;
	const spaceRightOfAnchorLeft = rootW - pad - anchor.left;

	let left = panelWidth > spaceRightOfAnchorLeft
		? anchor.right - panelWidth
		: anchor.left;

	left = Math.max(pad, Math.min(left, rootW - pad - panelWidth));

	let top = anchor.bottom + gap;

	if (top + panelHeight > rootH - pad) {
		top = anchor.top - panelHeight - gap;
	}

	top = Math.max(pad, Math.min(top, rootH - pad - panelHeight));

	panel.style.left = `${left}px`;
	panel.style.top = `${top}px`;
	panel.style.maxHeight = `${Math.max(120, rootH - pad - top)}px`;
}

/**
 * Open subcategory panel for one filter group.
 *
 * @param {HTMLElement} group
 * @param {string} blockId
 */
export function openFilterDropdownForGroup(group, blockId) {
	const filtersContainer = group.closest('.gallery-filters');
	const flyoutRoot = ensureFilterFlyoutRoot(blockId);

	closeFilterDropdown(filtersContainer, flyoutRoot);

	const panel = getFilterPanel(group);
	const toggle = group.querySelector('.gallery-filter-group__toggle');

	if (!panel || !toggle) {
		return;
	}

	panel.hidden = false;
	panel.classList.add('is-in-flyout');
	flyoutRoot.appendChild(panel);

	group._filterPanel = panel;
	group.classList.add('is-filter-open');
	toggle.setAttribute('aria-expanded', 'true');

	layoutFilterPanel(panel, group, flyoutRoot);
}

/**
 * @param {HTMLElement} filtersContainer
 * @param {string} blockId
 * @param {HTMLDivElement|null} flyoutRoot
 */
export function repositionOpenFilterDropdown(filtersContainer, blockId, flyoutRoot) {
	const group = getOpenFilterGroup(filtersContainer);
	if (!group) {
		return;
	}

	const root = flyoutRoot || ensureFilterFlyoutRoot(blockId);
	const panel = getFilterPanel(group);

	if (panel && root.contains(panel)) {
		layoutFilterPanel(panel, group, root);
	}
}
